import { ReactNode, useEffect, useRef, useState } from 'react';

import { Box, Card, CardProps, Heading, Link, Tag, TagLabel, TagLeftIcon, Text } from '@chakra-ui/react';

import { CheckIcon, ICON_SIZE } from '../icons';

import { MobileStickyBanner } from './mobile-sticky-banner';

export type StartLearningCardProps = {
  title: string;
  isMobile?: boolean;
  buttonSlot: ReactNode;
} & CardProps;

export function StartLearningCard({ title, isMobile = false, buttonSlot, ...cardProps }: StartLearningCardProps) {
  const [showMobileBanner, setShowMobileBanner] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!isMobile || !node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          setShowMobileBanner(!entry.isIntersecting && entry.boundingClientRect.top < 0);
        }
      },
      {
        threshold: 0,
      }
    );
    observer.observe(node);

    return () => observer.disconnect();
  }, [isMobile]);

  return (
    <>
      <Card
        ref={containerRef}
        bgColor='supportGray.10'
        border='1px solid'
        borderColor='supportBlue.20'
        boxShadow='none'
        display='flex'
        flexDirection='column'
        gap={3}
        p={3}
        {...cardProps}
      >
        <Tag alignSelf='flex-start' colorScheme='green'>
          <TagLeftIcon as={CheckIcon} height={ICON_SIZE} width={ICON_SIZE} />
          <TagLabel>You&apos;re subscribed</TagLabel>
        </Tag>
        <Heading as='p' size='heading-5'>
          {title}
        </Heading>
        <Text>
          This course is <Box as='b'>included</Box> in your plan along with the rest of our catalog.
        </Text>
        <Box pt={5}>{buttonSlot}</Box>
        <Box as='aside' borderTop='1px solid' borderTopColor='supportBlue.20' pt={4}>
          <Text>
            Need help?{' '}
            <Link href='https://support.udacity.com/hc/en-us/requests/new' isExternal>
              Contact Support
            </Link>
          </Text>
        </Box>
      </Card>
      {isMobile && <MobileStickyBanner buttonSlot={buttonSlot} isVisible={showMobileBanner} />}
    </>
  );
}
